/**
 * Chart & Table Type Validators — barChart, lineChart, pieChart,
 * tableReading, scheduleReading
 *
 * Split from typeStage.ts (2786 LOC god-file) — P2-S2
 */

import type { ValidationIssue } from './shared'
import { STAGE, blocker, coach } from './shared'

// ─── Table Reading Validation ─────────────────────────────────────────────────

export function validateTableReading(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[][] | undefined

  if (headers && Array.isArray(headers) && rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!Array.isArray(row)) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TABLE_ROW_NOT_ARRAY',
            `rows[${i}] must be an array`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (row.length !== headers.length) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_TABLE_ROW_LENGTH_MISMATCH',
            `rows[${i}] has ${row.length} cells but headers has ${headers.length}`,
            {
              field: `data.rows[${i}]`,
              expected: String(headers.length),
              actual: String(row.length),
              questionId: qId,
              familyId: fId,
              severity: 55,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Bar Chart Validation ─────────────────────────────────────────────────────

export function validateBarChart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const dataArray = data.data as unknown[] | undefined
  const labels = data.labels as unknown[] | undefined
  const values = data.values as unknown[] | undefined

  if (dataArray && Array.isArray(dataArray)) {
    for (let i = 0; i < dataArray.length; i++) {
      const entry = dataArray[i] as Record<string, unknown> | undefined
      if (!entry || typeof entry !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_BAR_CHART_INVALID_ENTRY',
            `data.data[${i}] must be an object with label and value`,
            {
              field: `data.data[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (
          typeof entry.label !== 'string' ||
          (entry.label as string).trim() === ''
        ) {
          issues.push(
            coach(
              'type',
              STAGE,
              'TYPE_BAR_CHART_EMPTY_LABEL',
              `data.data[${i}].label should be a non-empty string`,
              {
                field: `data.data[${i}].label`,
                questionId: qId,
                familyId: fId,
                severity: 45,
              },
            ),
          )
        }
        if (typeof entry.value !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_BAR_CHART_VALUE_NOT_NUMBER',
              `data.data[${i}].value must be a number`,
              {
                field: `data.data[${i}].value`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
    if (dataArray.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_BAR_CHART_TOO_FEW',
          'Bar chart should have at least 2 data entries',
          {
            field: 'data.data',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }
  } else if (
    labels &&
    Array.isArray(labels) &&
    values &&
    Array.isArray(values)
  ) {
    if (labels.length !== values.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_BAR_CHART_LENGTH_MISMATCH',
          `labels (${labels.length}) and values (${values.length}) must have same length`,
          {
            field: 'data.values',
            expected: String(labels.length),
            actual: String(values.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    for (let i = 0; i < values.length; i++) {
      if (typeof values[i] !== 'number') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_BAR_CHART_VALUE_NOT_NUMBER',
            `values[${i}] must be a number`,
            {
              field: `data.values[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  } else if (!dataArray && !labels) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_BAR_CHART_NO_DATA',
        'Bar chart requires either data.data array or data.labels + data.values arrays',
        {
          field: 'data.data',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Line Chart Validation ────────────────────────────────────────────────────

export function validateLineChart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const dataArray = data.data as unknown[] | undefined
  const labels = data.labels as unknown[] | undefined
  const values = data.values as unknown[] | undefined

  if (dataArray && Array.isArray(dataArray)) {
    for (let i = 0; i < dataArray.length; i++) {
      const entry = dataArray[i] as Record<string, unknown> | undefined
      if (!entry || typeof entry !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_LINE_CHART_INVALID_ENTRY',
            `data.data[${i}] must be an object with label and value`,
            {
              field: `data.data[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (typeof entry.value !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_LINE_CHART_VALUE_NOT_NUMBER',
              `data.data[${i}].value must be a number`,
              {
                field: `data.data[${i}].value`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
    if (dataArray.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_LINE_CHART_TOO_FEW',
          'Line chart should have at least 2 data points',
          {
            field: 'data.data',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }
  } else if (
    labels &&
    Array.isArray(labels) &&
    values &&
    Array.isArray(values)
  ) {
    if (labels.length !== values.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_LINE_CHART_LENGTH_MISMATCH',
          `labels (${labels.length}) and values (${values.length}) must have same length`,
          {
            field: 'data.values',
            expected: String(labels.length),
            actual: String(values.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    for (let i = 0; i < values.length; i++) {
      if (typeof values[i] !== 'number') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_LINE_CHART_VALUE_NOT_NUMBER',
            `values[${i}] must be a number`,
            {
              field: `data.values[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  } else if (!dataArray && !labels) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_LINE_CHART_NO_DATA',
        'Line chart requires either data.data array or data.labels + data.values arrays',
        {
          field: 'data.data',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Schedule Reading Validation ──────────────────────────────────────────────

export function validateScheduleReading(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[][] | undefined

  if (headers && Array.isArray(headers) && rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!Array.isArray(row)) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_SCHEDULE_ROW_NOT_ARRAY',
            `rows[${i}] must be an array`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (row.length !== headers.length) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_SCHEDULE_ROW_LENGTH_MISMATCH',
            `rows[${i}] has ${row.length} cells but headers has ${headers.length}`,
            {
              field: `data.rows[${i}]`,
              expected: String(headers.length),
              actual: String(row.length),
              questionId: qId,
              familyId: fId,
              severity: 55,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Pie Chart Validation ─────────────────────────────────────────────────────

export function validatePieChart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const labels = data.labels as unknown[] | undefined
  const values = data.values as unknown[] | undefined
  const correctIndex = data.correctIndex

  if (labels && Array.isArray(labels) && values && Array.isArray(values)) {
    if (labels.length !== values.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_PIE_CHART_LENGTH_MISMATCH',
          `labels (${labels.length}) and values (${values.length}) must have same length`,
          {
            field: 'data.values',
            expected: String(labels.length),
            actual: String(values.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < values.length; i++) {
      if (typeof values[i] !== 'number') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_PIE_CHART_VALUE_NOT_NUMBER',
            `values[${i}] must be a number`,
            {
              field: `data.values[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }

    for (let i = 0; i < labels.length; i++) {
      if (
        typeof labels[i] !== 'string' ||
        (labels[i] as string).trim() === ''
      ) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_PIE_CHART_EMPTY_LABEL',
            `labels[${i}] should be a non-empty string`,
            {
              field: `data.labels[${i}]`,
              questionId: qId,
              familyId: fId,
              severity: 45,
            },
          ),
        )
      }
    }

    if (typeof correctIndex === 'number') {
      if (
        correctIndex < 0 ||
        correctIndex >= labels.length ||
        !Number.isInteger(correctIndex)
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_PIE_CHART_INVALID_CORRECT_INDEX',
            `correctIndex ${correctIndex} out of range [0, ${labels.length - 1}]`,
            {
              field: 'data.correctIndex',
              expected: `0-${labels.length - 1}`,
              actual: String(correctIndex),
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    } else if (correctIndex === undefined || correctIndex === null) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_PIE_CHART_MISSING_CORRECT_INDEX',
          'Pie chart requires data.correctIndex',
          {
            field: 'data.correctIndex',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (labels && Array.isArray(labels) && labels.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_PIE_CHART_TOO_FEW_SLICES',
        'Pie chart should have at least 2 slices',
        {
          field: 'data.labels',
          questionId: qId,
          familyId: fId,
          severity: 40,
        },
      ),
    )
  }

  return issues
}
